import React, { useEffect, useRef } from 'react';
import {
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Tooltip,
    Menu,
    MenuItem,
    CircularProgress
} from '@material-ui/core';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { convertToLocalTime } from '../../../helpers/timeUtils';
import { useSelector, useDispatch } from 'react-redux';
import { Close, Delete } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import AddBoxOutlinedIcon from '@material-ui/icons/AddBoxOutlined';
import useSubscriberList from '../useSubscriberLists';
import { updatePageNo } from 'store/helpers/tableAndToolbar/actions';
import { getActiveSubscribers, getList } from 'store/lists/actions';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500]
    },
    saveButton: {
        marginRight: '16px',
        marginBottom: '12px'
    },
    inputGrid: {
        maxWidth: '30rem'
    },
    actionIcon: {
        margin: '0px 8px',
        fontWeight: 'normal',
        opacity: '1 !important',
        color: theme.palette.primary.main + '!important',
        '&.Mui-disabled': {
            color: 'rgba(0,0,0,.55) !important'
        },
    },
    deleteButton: {
        color: theme.palette.error.main
    },
    menuPaper: {
        maxHeight: '300px',
        overflowY: 'auto',
        overflowX: 'hidden'
    }
}))

const AddSubscriberListModal = props => {
    const { isOpen, closeCreateListModal, listId, type, onSaveSuccess } = props;
    const classes = useStyles();
    const anchorRefE2 = useRef(null);
    const dispatch = useDispatch();


    const isLoading = useSelector(store => store.schema.gettingPaginated);
    const isCreateSucceeded = useSelector(store => store.lists.subscribersList.listCreated);
    const isUpdateSucceeded = useSelector(store => store.lists.subscribersList.listUpdated);
    const subscriberListData = useSelector(store => store.lists.additionalProfileList.profileList || {});

    const {
        subscriberList,
        selectedOptionalFields,
        selectOpen,
        mandatoryTextFields,
        availableOptionalFields,
        setSelectOpen,
        handleFieldSelect,
        deleteField,
        handleChange,
        handleDateChange,
        onClickSave,
        onBlur,
        shouldShowError,
        getErrorMessage,
        areAllMandatoryFieldsFilled,
        formController
    } = useSubscriberList(listId, subscriberListData.additionalProfileData, type);

    useEffect(() => {
        if (isCreateSucceeded || isUpdateSucceeded) {
            closeCreateListModal();
            dispatch(updatePageNo(0));
            dispatch(getList(listId));
            if (onSaveSuccess) {
                onSaveSuccess();
            } else {
                dispatch(getActiveSubscribers(listId));
            }
        }
    }, [isCreateSucceeded, isUpdateSucceeded, closeCreateListModal, onSaveSuccess]);
    return (
        <Dialog
            open={isOpen}
            aria-labelledby="form-dialog-title"
            fullWidth
            maxWidth="sm">
            <Typography variant="h2">
                <DialogTitle id="form-dialog-title" disableTypography={true}>
                    {type === 'create' ? 'Create Subscriber List' : 'Edit Subscriber List'}
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={closeCreateListModal}>
                    <Close />
                </IconButton>
            </Typography>
            <DialogContent>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <div>
                        {mandatoryTextFields?.map(field => (
                            <TextField
                                key={field.name}
                                className={classes.inputGrid}
                                fullWidth
                                label={field.displayName}
                                margin="dense"
                                name={field.name}
                                variant="outlined"
                                required
                                value={subscriberList[field.name] || ''}
                                onChange={handleChange}
                                onFocus={(e) => formController.onFocus(e)}
                                onBlur={onBlur}
                                error={shouldShowError(field.name)}
                                helperText={getErrorMessage(field.name)}
                            ></TextField>

                        ))}
                        {selectedOptionalFields?.map(optField => (
                            <div key={optField.name}>
                                {optField.name.toLowerCase().includes('birthdate') || 
                                 optField.name.toLowerCase().includes('birth_date') ||
                                 optField.name.toLowerCase().includes('dateofbirth') ? (
                                    <MuiPickersUtilsProvider utils={MomentUtils}>
                                        <KeyboardDateTimePicker
                                            key={optField.name}
                                            className={classes.inputGrid}
                                            margin="dense"
                                            inputVariant="outlined"
                                            label={optField.displayName}
                                            format="MM/DD/YYYY hh:mm a"
                                            name={optField.name}
                                            value={convertToLocalTime(subscriberList[optField.name])}
                                            onChange={(date) => handleDateChange(date, optField.name)}
                                            onFocus={(e) => formController.onFocus(e)}
                                            onBlur={onBlur}
                                            error={shouldShowError(optField.name)}
                                            helperText={getErrorMessage(optField.name)}
                                            KeyboardButtonProps={{
                                                'aria-label': 'change date and time',
                                            }}
                                            fullWidth
                                        />
                                    </MuiPickersUtilsProvider>
                                ) : (
                                    <TextField
                                        key={optField.name}
                                        className={classes.inputGrid}
                                        fullWidth
                                        label={optField.displayName}
                                        margin="dense"
                                        name={optField.name}
                                        variant="outlined"
                                        value={subscriberList[optField.name] || ''}
                                        onChange={handleChange}
                                        onFocus={(e) => formController.onFocus(e)}
                                        onBlur={onBlur}
                                        error={shouldShowError(optField.name)}
                                        helperText={optField.type === 'tags' 
                                            ? 'Enter tags separated by commas (e.g., tag1, tag2, tag3)' 
                                            : getErrorMessage(optField.name)}
                                        placeholder={optField.type === 'tags' ? 'tag1, tag2, tag3' : ''}
                                    />
                                )}
                                <IconButton
                                    className={classes.deleteButton}
                                    onClick={() => deleteField(optField.name)}>
                                    <Delete />
                                </IconButton>
                            </div>
                        ))}
                        <Tooltip title="Add fields">
                            <Button
                                className={classes.actionIcon}
                                endIcon={<AddBoxOutlinedIcon />}
                                onClick={() => setSelectOpen(true)}
                                ref={anchorRefE2}
                                disabled={availableOptionalFields.length === 0}
                            > Add
                            </Button>
                        </Tooltip>
                        {selectOpen && (
                            <Menu
                                keepMounted
                                open={selectOpen}
                                onClose={() => setSelectOpen(false)}
                                anchorEl={anchorRefE2.current}
                                getContentAnchorEl={null}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right'
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left'
                                }}
                                PaperProps={{
                                    className: classes.menuPaper
                                }}>
                                {availableOptionalFields.length === 0 && (
                                    <MenuItem disabled>No available fields</MenuItem>
                                )}
                                {availableOptionalFields.map((field) => (
                                    <MenuItem
                                        key={field.name}
                                        onClick={() => handleFieldSelect(field)}
                                    >
                                        {field.displayName}
                                    </MenuItem>
                                ))}
                            </Menu>
                        )}
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    className={classes.saveButton}
                    color="primary"
                    variant="contained"
                    disabled={!areAllMandatoryFieldsFilled() || isLoading}
                    onClick={onClickSave}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AddSubscriberListModal;